import { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Button,
  Switch,
  Typography,
  Alert,
  InputAdornment,
} from "@mui/material";

import { motion } from "framer-motion";
import { toast } from "react-toastify";
import apiService from "../../../../../../service/Api";
import CustomTextField from "../../../../../../components/ui/Textfield";
import GoFundMeWidget from "../../../../../../components/GoFundMeWidget";
import DonationsTable from "../../components/chema/DonationsTable";


interface DonationsTabProps {
  streamData: {
    go_fund_me_link?: string;
    go_fund_me_enabled: boolean;
    chema_fund_enabled?: boolean;
  } | null;
  streamId: string;
  isAdmin: boolean;
  isMobile: boolean;
  fetchStreamDetails: () => Promise<void>;
  donationRefreshKey?: number;
  highlightDonationId?: number;
}

export default function DonationsTab({
  streamData,
  streamId,
  isAdmin,
  isMobile,
  fetchStreamDetails,
  donationRefreshKey = 0,
  highlightDonationId,
}: DonationsTabProps) {

  const [goFundMeLink, setGoFundMeLink] = useState(
    streamData?.go_fund_me_link || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [chemaEmail, setChemaEmail] = useState("");
  const [enablingChema, setEnablingChema] = useState(false);
  const [fundraiser, setFundraiser] = useState<{
    id: number;
    owner_email: string | null;
    owner_display_name: string;
  } | null>(null);

  // The native Chema fundraiser attached to this stream (if any). Its existence
  // means the fundraiser was already created, so we swap the create form for a
  // read-only owner panel.
  const loadFundraiser = useCallback(async () => {
    if (!streamId) return;
    try {
      const res = await apiService.get<{
        id: number;
        owner_email: string | null;
        owner_display_name: string;
      }>(`/api/chema/campaigns/by-stream/${streamId}/`);
      setFundraiser(res.data);
    } catch {
      setFundraiser(null);
    }
  }, [streamId]);

  useEffect(() => {
    loadFundraiser();
  }, [loadFundraiser]);

  const handleSaveLink = async () => {
    if (!goFundMeLink || !goFundMeLink.includes("gofundme.com")) {
      setError("Please enter a valid GoFundMe URL");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      const payload = { go_fund_me_link: goFundMeLink };
      await apiService.patch(`/api/streams/stream/${streamId}/`, payload);
      toast.success("GoFundMe link saved successfully");
      await fetchStreamDetails();
    } catch (err: unknown) {
      console.error("Failed to save link:", err);
      let errorMessage = "Failed to save GoFundMe link";
      if (err instanceof Object && 'response' in err && typeof err.response === 'object' && err.response !== null && 'data' in err.response && typeof err.response.data === 'object' && err.response.data !== null && 'detail' in err.response.data) {
        errorMessage = (err.response.data as Record<string, unknown>).detail as string;
      }
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleGoFundMeEnabled = async (newValue: boolean) => {
    try {
      await apiService.patch(`/api/streams/stream/${streamId}/`, {
        go_fund_me_enabled: newValue,
      });
      toast.success(
        `Donations ${newValue ? "enabled" : "disabled"} successfully`
      );
      await fetchStreamDetails();
    } catch (err) {
      console.error("Failed to toggle donation setting:", err);
      toast.error("Failed to update donation setting.");
    }
  };

  // Create a native Chema fundraiser for this stream and enable its widget.
  // An optional client email makes that client the fundraiser owner (they get a
  // platform:chema account + an invite email); otherwise the staff user owns it.
  const handleEnableChemaFundraiser = async () => {
    setEnablingChema(true);
    try {
      await apiService.post(`/api/chema/campaigns/enable-for-booking/`, {
        stream_id: streamId,
        client_email: chemaEmail.trim() || undefined,
        fundraiser_type: "ongoing",
      });
      toast.success("Chema fundraiser enabled for this stream.");
      setChemaEmail("");
      await Promise.all([fetchStreamDetails(), loadFundraiser()]);
    } catch (err) {
      console.error("Failed to enable Chema fundraiser:", err);
      toast.error("Failed to enable Chema fundraiser.");
    } finally {
      setEnablingChema(false);
    }
  };

  const handleToggleChemaEnabled = async (newValue: boolean) => {
    if (newValue) {
      await handleEnableChemaFundraiser();
      return;
    }
    try {
      await apiService.patch(`/api/streams/stream/${streamId}/`, {
        chema_fund_enabled: false,
      });
      toast.success("Chema fundraiser paused and hidden.");
      await Promise.all([fetchStreamDetails(), loadFundraiser()]);
    } catch (err) {
      console.error("Failed to toggle Chema setting:", err);
      toast.error("Failed to update Chema setting.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ height: "100%", boxShadow: 0, borderRadius: "20px" }}>
        <CardContent>
          {/* Admin Section */}
          {isAdmin && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Legacy GoFundMe link
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                Embeds an external GoFundMe page. Separate from the native Chema
                fundraiser below.
              </Typography>
              <CustomTextField
                label="GoFundMe Embed URL"
                value={goFundMeLink}
                onChange={(e) => {
                  setGoFundMeLink(e.target.value);
                }}
                fullWidth
                margin="normal"
                placeholder="https://www.gofundme.com/f/campaign-name or widget URL"
                helperText="Enter the GoFundMe campaign URL or widget embed URL"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={handleSaveLink}
                        variant="contained"
                        size="small"
                        disabled={isSaving}
                      >
                        {isSaving
                          ? "Saving..."
                          : streamData?.go_fund_me_link
                          ? "Update"
                          : "Save"}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  my: 2,
                }}
              >
                <Typography sx={{ mr: 2 }}>Enable Donations</Typography>
                <Switch
                  checked={streamData?.go_fund_me_enabled || false}
                  onChange={(e) =>
                    handleToggleGoFundMeEnabled(e.target.checked)
                  }
                  color="primary"
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Native Chema fundraiser (independent of GoFundMe) */}
              <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #eee" }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  Chema Fundraiser
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                  Create a native Chema fundraiser for this stream. Add the client&apos;s
                  email to make them the owner (they get an invite); leave blank to own it
                  yourself.
                </Typography>
                {fundraiser ? (
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Invited client
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {fundraiser.owner_email ||
                        fundraiser.owner_display_name ||
                        "Owned by you (no client invited)"}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <CustomTextField
                      label="Client email (optional)"
                      value={chemaEmail}
                      onChange={(e) => setChemaEmail(e.target.value)}
                      fullWidth
                      margin="normal"
                      placeholder="client@example.com"
                    />
                    <Button
                      variant="contained"
                      onClick={handleEnableChemaFundraiser}
                      disabled={enablingChema}
                      sx={{ mt: 1 }}
                    >
                      {enablingChema ? "Enabling…" : "Create & enable fundraiser"}
                    </Button>
                  </>
                )}

                <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                  <Typography sx={{ mr: 2 }}>Show Chema widget</Typography>
                  <Switch
                    checked={streamData?.chema_fund_enabled || false}
                    onChange={(e) => handleToggleChemaEnabled(e.target.checked)}
                    disabled={enablingChema}
                    color="primary"
                  />
                </Box>
              </Box>

            </Box>
          )}

          {streamData?.go_fund_me_link && isMobile && (
            <Box sx={{ mt: 4 }}>
              <GoFundMeWidget url={streamData.go_fund_me_link} />
            </Box>
          )}

        </CardContent>
        {(streamData?.chema_fund_enabled || streamData?.go_fund_me_enabled) &&
          streamId && (
            <DonationsTable
              streamId={streamId}
              campaignId={fundraiser?.id}
              title="All Donations"
              ordering="-created_at"
              refreshKey={donationRefreshKey}
              highlightDonationId={highlightDonationId}
            />
          )}
      </Card>
    </motion.div>
  );
}